/**
 * Shared constants and types of the Purchasing → Forecast grid.
 *
 * Record types: 1 = Forecast, 2 = Open PO, 3 = Total (Open PO + Forecast).
 */
import { ForecastTableRow } from '../../../../lib/api';
import type { ForecastGridOptions } from '../../../../lib/api/types/forecast-types';
import { StockStatus } from '../../StockStatusIndicator';

export type { ForecastGridOptions };

/** Row tint per record type. */
export const RECORD_TYPE_COLORS: Record<number, string> = {
  1: 'bg-blue-50 text-blue-800',      // Forecast
  2: 'bg-green-50 text-green-800',    // Open PO
  3: 'bg-purple-50 text-purple-800 font-semibold',  // Total
};

export const RECORD_TYPE_LABELS: Record<number, string> = {
  1: 'Forecast',
  2: 'Open PO',
  3: 'Total',
};

/**
 * Record type sort order inside a part block: Open PO first, then Forecast,
 * then Total — so Open PO lines are shown before Forecast lines for each part.
 */
export const RECORD_TYPE_ORDER: Record<number, number> = {
  2: 0,
  1: 1,
  3: 2,
};

/** A table row with the optional stock indicator the live path adds. */
export interface ForecastTableRowWithStock extends ForecastTableRow {
  stock_status?: StockStatus;
}

/** One week / month column of the grid. */
export interface ForecastGridColumn {
  key: string;
  label: string;
  /** "start to end" of the bucket, shown on header hover. */
  tooltip: string;
}

/** Which optional columns / rows the grid renders. */
export interface ForecastGridLayout {
  showPoNumber: boolean;
  showSupplierPartNo: boolean;
  showStockStatus: boolean;
  /** Firm days + Plant — the Part Schedule module (schedule-line tenants). */
  showFirmDays: boolean;
  weeksOnly: boolean;
  weekColumns: ForecastGridColumn[];
  monthColumns: ForecastGridColumn[];
}

/** Draft / applied filter state of the Forecast tab. */
export interface ForecastFilters {
  partNumber: string;
  /** Contributing OPEN PO number — finds the part blocks it sits on. */
  poNumber: string;
  supplierPartNumber: string;
  /** Toolbar quick search — part number, supplier part no. or PO number. */
  partSearch: string;
  commodityGroup: string;
  commodityType: string;
  supplierGroup: string;
  supplierId: string;
  customerId: string;
  recordType: string;
  importDateFrom: string;
  importDateTo: string;
  /** CR-6448: the supplier panel's single Import Date (which import snapshot to show). */
  importDate: string;
  /** CR-6448: warehouse code — forecast rows and open PO delivery warehouses. */
  warehouse: string;
  /**
   * CR-6440 rework: the customer's supplier code, matched exactly — the open PO
   * line's own code, or the supplier a code is issued to for forecast rows.
   */
  supplierCode: string;
  /** Forecast (delivery) date window — trims the visible week/month columns. */
  dateFrom: string;
  dateTo: string;
  // CR-6440: cascade filter fields
  division: string;
  companyCountry: string;
  site: string;
  supplierCountry: string;
}

export const EMPTY_FORECAST_FILTERS: ForecastFilters = {
  partNumber: '',
  poNumber: '',
  supplierPartNumber: '',
  partSearch: '',
  commodityGroup: '',
  commodityType: '',
  supplierGroup: '',
  supplierId: '',
  customerId: '',
  recordType: '',
  importDateFrom: '',
  importDateTo: '',
  importDate: '',
  warehouse: '',
  supplierCode: '',
  dateFrom: '',
  dateTo: '',
  division: '',
  companyCountry: '',
  site: '',
  supplierCountry: '',
};

/**
 * Reads a field the row type does not declare — the dynamic bucket keys
 * (wk1…, month1…) and the identity strings (customer, supplier, uom).
 *
 * @param row - a forecast table row
 * @param key - the field name
 * @returns the value, or undefined when absent
 */
export function rowField(row: ForecastTableRow, key: string): string | undefined {
  const value = (row as unknown as Record<string, unknown>)[key];
  return value === undefined || value === null ? undefined : String(value);
}

/**
 * Numeric id the row carries but the type does not declare (customer_id,
 * supplier_id).
 *
 * @param row - a forecast table row
 * @param key - the field name
 * @returns the id, or undefined when absent / zero ("Unassigned")
 */
export function rowId(row: ForecastTableRow, key: string): number | undefined {
  const value = Number((row as unknown as Record<string, unknown>)[key]);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}
